import React, { useState, useEffect } from 'react';
import axiosInstance from '../apis/axiosConfig';
import { toast } from 'react-toastify';
import { FaFileContract, FaSearch, FaMapMarkerAlt, FaUser, FaTrash } from 'react-icons/fa';
import '../pages_premium.css';
import './EnquiriesList.css';
import AdminPagination from '../components/common/AdminPagination';

export default function EnquiriesList({ type }) {
    const [enquiries, setEnquiries] = useState([]);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    useEffect(() => {
        setCurrentPage(1);
    }, [search, type]);

    const fetchEnquiries = async () => {
        try {
            setLoading(true);
            const res = await axiosInstance.get('/leads');
            const filtered = res.data.filter(lead => 
                lead.Property?.listing_role?.toLowerCase() === type.toLowerCase()
            );
            setEnquiries(filtered);
        } catch (error) {
            console.error('Error fetching enquiries:', error);
            toast.error('Failed to load enquiries');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchEnquiries();
    }, [type]);

    const deleteEnquiry = async (id) => {
        if (!window.confirm('Are you sure you want to delete this enquiry?')) return;
        try {
            await axiosInstance.delete(`/leads/${id}`);
            toast.success('Enquiry deleted successfully');
            fetchEnquiries();
        } catch (error) {
            console.error('Error deleting enquiry:', error);
            toast.error('Failed to delete enquiry');
        }
    };

    const getPageTitle = () => {
        if (type === 'student') return 'Student Enquiries';
        if (type === 'owner') return 'Owner Enquiries';
        if (type === 'agent') return 'Agent Enquiries';
        return 'Property Enquiries';
    };

    const filtered = enquiries.filter(e =>
        e.Student?.name?.toLowerCase().includes(search.toLowerCase()) ||
        e.Student?.email?.toLowerCase().includes(search.toLowerCase()) ||
        e.Property?.title?.toLowerCase().includes(search.toLowerCase())
    );

    const paginatedEnquiries = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

    return (
        <div className="page-premium-wrapper enquiries-list-page">
            <div className="page-premium-content">

                {/* Hero Header */}
                <div className="page-hero-header page-hero-properties">
                    <div className="ph-blob ph-blob-1"></div>
                    <div className="ph-blob ph-blob-2"></div>

                    <div className="ph-left">
                        <div className="ph-icon">
                            <FaFileContract size={26} />
                        </div>
                        <div>
                            <h1 className="ph-title">{getPageTitle()}</h1>
                            <p className="ph-subtitle">Manage enquiries submitted by users for {type} properties</p>
                        </div>
                    </div>

                    <div className="ph-right">
                        <div className="ph-count-badge">
                            <span className="count-num">{enquiries.length}</span>
                            <span className="count-lbl">Total Enquiries</span>
                        </div>
                    </div>
                </div>

                {/* Table Card */}
                <div className="premium-table-card">
                    <div className="ptable-toolbar">
                        <div className="ptable-title">
                            <span className="ptable-title-dot"></span>
                            {getPageTitle()} List
                        </div>
                        <div className="ptable-search">
                            <FaSearch className="ptable-search-icon" />
                            <input
                                type="text"
                                placeholder="Search by name, email, or property..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="ptable-wrap">
                        <table className="premium-table">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th style={{ minWidth: '200px' }}>Property</th>
                                    <th style={{ minWidth: '180px' }}>Enquired By</th>
                                    <th style={{ minWidth: '180px' }}>Listed By</th>
                                    <th style={{ minWidth: '220px' }}>Message</th>
                                    <th>Status</th>
                                    <th>Date</th>
                                    <th style={{ textAlign: 'center' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan="8" style={{ textAlign: 'center', padding: '36px', color: '#64748b' }}>
                                            Loading enquiries...
                                        </td>
                                    </tr>
                                ) : filtered.length === 0 ? (
                                    <tr>
                                        <td colSpan="8">
                                            <div className="ptable-empty">
                                                <div className="ptable-empty-icon">📑</div>
                                                <p>No enquiries found.</p>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    paginatedEnquiries.map((enq, idx) => (
                                        <tr key={enq.id}>
                                            <td style={{ color: '#9ba6c0', fontWeight: 600 }}>{(currentPage - 1) * pageSize + idx + 1}</td>
                                            <td>
                                                <div className="d-flex flex-column">
                                                    <strong style={{ color: '#0f172a' }}>{enq.Property?.title || 'Unknown Property'}</strong>
                                                    <span style={{ fontSize: '12px', color: '#64748b' }}>
                                                        <FaMapMarkerAlt style={{ marginRight: 3, color: '#f97316' }} />
                                                        {enq.Property?.address || 'N/A'}
                                                    </span>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="d-flex align-items-center gap-2">
                                                    <div style={{ background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '50%', width: '30px', height: '30px', color: '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', flexShrink: 0 }}>
                                                        <FaUser size={12} />
                                                    </div>
                                                    <div className="d-flex flex-column">
                                                        <strong style={{ color: '#0f172a' }}>{enq.Student?.name || 'N/A'}</strong>
                                                        <span style={{ fontSize: '12px', color: '#64748b' }}>
                                                            {enq.Student?.email || 'N/A'}
                                                        </span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="d-flex flex-column">
                                                    <strong style={{ color: '#0f172a' }}>{enq.Agent?.name || 'N/A'}</strong>
                                                    <span style={{ fontSize: '12px', color: '#64748b' }}>
                                                        {enq.Agent?.email || 'N/A'}
                                                    </span>
                                                </div>
                                            </td>
                                            <td style={{ color: '#475569', fontSize: '0.85rem', lineHeight: '1.4' }}>
                                                {enq.message || 'No message provided.'}
                                            </td>
                                            <td>
                                                <span className={`ptag ${
                                                    enq.status === 'new' ? 'ptag-student' : enq.status === 'contacted' ? 'ptag-premium' : 'ptag-closed'
                                                }`}>
                                                    {enq.status}
                                                </span>
                                            </td>
                                            <td style={{ color: '#64748b', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                                                {enq.createdAt ? new Date(enq.createdAt).toLocaleDateString() : 'N/A'}
                                            </td>
                                            <td>
                                                <div className="enq-actions">
                                                    <button
                                                        className="action-btn action-btn-delete"
                                                        data-tooltip="Delete Enquiry"
                                                        onClick={() => deleteEnquiry(enq.id)}
                                                    >
                                                        <FaTrash />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                    <AdminPagination
                        currentPage={currentPage}
                        totalItems={filtered.length}
                        pageSize={pageSize}
                        onPageChange={setCurrentPage}
                        onPageSizeChange={setPageSize}
                        itemLabel="enquiries"
                    />
                </div>

            </div>
        </div>
    );
}
